import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';

interface TrialBalanceProps {
  entrepriseId: string;
  exerciceId: string;
}

const formatCurrency = (centimes: number) => {
  const euros = centimes / 100;
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(euros);
};

const TrialBalance: React.FC<TrialBalanceProps> = ({ entrepriseId, exerciceId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['trial-balance', entrepriseId, exerciceId],
    queryFn: () => api.trialBalance.get(entrepriseId, exerciceId),
  });

  if (isLoading) return <div className="text-sm text-gray-500">Chargement...</div>;
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur: {(error as Error).message}</div>;
  if (!data) return null;

  const { entries, total_debit, total_credit, is_balanced } = data;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">Balance de vérification</h2>
      <p className="text-sm text-gray-500 mb-6">Exercice: {exerciceId}</p>

      <div className="flex flex-wrap gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-3">
          <span className="text-sm text-gray-500">Total Débit</span>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(total_debit)}</p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 px-5 py-3">
          <span className="text-sm text-gray-500">Total Crédit</span>
          <p className="text-lg font-bold text-gray-900">{formatCurrency(total_credit)}</p>
        </div>
        <div className={`rounded-xl px-5 py-3 ${is_balanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
          <p className={`text-lg font-bold ${is_balanced ? 'text-green-600' : 'text-red-600'}`}>{is_balanced ? 'Équilibrée' : 'Déséquilibre'}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Compte</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Débit</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Crédit</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e: any) => (
              <tr key={e.compte_id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 font-mono text-xs">{e.compte_id}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{e.libelle}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(e.debit_centimes)}</td>
                <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(e.credit_centimes)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="bg-gray-50 font-bold">
              <td colSpan={2} className="px-4 py-3 text-gray-900">TOTAUX</td>
              <td className="px-4 py-3 text-gray-900 text-right">{formatCurrency(total_debit)}</td>
              <td className="px-4 py-3 text-gray-900 text-right">{formatCurrency(total_credit)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default TrialBalance;
